import { useEffect, useRef, type ReactNode } from 'react'
import { useNetworkStatus } from '../../providers/NetworkStatusProvider'
import { useI18n } from '../../i18n/I18nContext'
// Available before the connection drops; a lazy offline page cannot load offline.
import OfflinePage from '../../pages/error/OfflinePage'
import ServerErrorPage from '../../pages/error/ServerErrorPage'

export default function NetworkRecoveryBoundary({ children }: { children: ReactNode }) {
    const { isOffline, hasServerError } = useNetworkStatus()
    const { t } = useI18n()
    const blocked = isOffline || hasServerError
    const dialog = useRef<HTMLDivElement>(null)
    const previousFocus = useRef<HTMLElement | null>(null)

    useEffect(() => {
        if (blocked) return
        const remember = () => { previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null }
        remember()
        document.addEventListener('focusin', remember)
        return () => document.removeEventListener('focusin', remember)
    }, [blocked])

    useEffect(() => {
        if (!blocked) return
        const overflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        dialog.current?.focus()
        return () => {
            document.body.style.overflow = overflow
            if (previousFocus.current?.isConnected) previousFocus.current.focus()
        }
    }, [blocked])

    return <>
        <div inert={blocked} aria-hidden={blocked || undefined}>{children}</div>
        {blocked && <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true"
            aria-label={t(isOffline ? 'errorPage.offline.title' : 'errorPage.serverError.title')}
            className="fixed inset-0 z-[150] overflow-y-auto bg-[var(--dhtn-canvas)] outline-none">
            {isOffline ? <OfflinePage onRetry={() => {
                if (navigator.onLine) window.dispatchEvent(new Event('online'))
            }} /> : <ServerErrorPage />}
        </div>}
    </>
}
